import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../contexts/LanguageContext';

const SignupPage = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (password !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      setLoading(false);
      return;
    }
    
    if (password.length < 8) {
      setError(t('passwordMinLength'));
      setLoading(false);
      return;
    }
    
    const { error } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        data: {
          has_password: true,
          has_signed_in: false
        }
      }
    });
    setLoading(false);
    if (error) {
      setError(error.message || t('signupFailed'));
    } else {
      // Show success message or redirect
      alert(t('checkEmailConfirmAccount'));
      navigate('/login');
    }
  };

  useEffect(() => {
    let cancelled = false;
    let rendered = false;
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const tryInit = () => {
      if (cancelled) return;
      if (!clientId) return;
      if (!window.google || !window.google.accounts || !window.google.accounts.id) {
        setTimeout(tryInit, 250);
        return;
      }
      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async ({ credential }) => {
            setError('');
            setLoading(true);
            try {
              const { error } = await supabase.auth.signInWithIdToken({ provider: 'google', token: credential });
              if (error) {
                setError(error.message || t('googleAuthFailed'));
              } else {
                try {
                  const { data: userData } = await supabase.auth.getUser();
                  const user = userData?.user;
                  const email = user?.email;
                  const hasPassword = user?.user_metadata?.has_password === true;
                  const hasSignedIn = user?.user_metadata?.has_signed_in === true;
                  if (user && !hasPassword && email) {
                    await supabase.auth.updateUser({ data: { has_password: false, has_signed_in: false } });
                    navigate(`/create-password?email=${encodeURIComponent(email)}`);
                    return;
                  }
                  if (user && !hasSignedIn) {
                    navigate('/onboarding');
                    return;
                  }
                } catch (_) {}
                navigate('/');
              }
            } catch (_e) {
              setError(t('googleAuthFailed'));
            } finally {
              setLoading(false);
            }
          }
        });
        if (googleBtnRef.current && !rendered) {
          window.google.accounts.id.renderButton(googleBtnRef.current, {
            theme: 'outline',
            size: 'large',
            type: 'standard',
            shape: 'rectangular',
            text: 'continue_with',
          });
          rendered = true;
          setGsiRendered(true);
        }
      } catch (_) {}
    };
    tryInit();
    return () => { cancelled = true; };
  }, [navigate, t]);
  const googleBtnRef = useRef(null);
  const [gsiRendered, setGsiRendered] = useState(false);

  // Removed secondary button; rely on official GIS button only

  return (
    <div style={{ maxWidth: 420, margin: '96px auto', background: '#fff', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', padding: '2.5rem 2rem', textAlign: 'center' }}>
      <h2 style={{ marginBottom: 24, color: '#2c6e49', fontWeight: 600 }}>{t('signUp')}</h2>
      {error && <div style={{ color: '#e57373', marginBottom: 16 }}>{error}</div>}
      <form onSubmit={handleSignup}>
        <input
          type="email"
          placeholder={t('email')}
          value={email}
          onChange={e => setEmail(e.target.value)}
          style={{ width: '100%', padding: '0.8rem', marginBottom: 12, borderRadius: 8, border: '1px solid #ccc', fontSize: '1rem', fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif', boxSizing: 'border-box' }}
          autoFocus
        />
        <input
          type="password"
          placeholder={t('password')}
          value={password}
          onChange={e => setPassword(e.target.value)}
          style={{ width: '100%', padding: '0.8rem', marginBottom: 12, borderRadius: 8, border: '1px solid #ccc', fontSize: '1rem', fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif', boxSizing: 'border-box' }}
        />
        <input
          type="password"
          placeholder={t('confirmPassword')}
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          style={{ width: '100%', padding: '0.8rem', marginBottom: 20, borderRadius: 8, border: '1px solid #ccc', fontSize: '1rem', fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif', boxSizing: 'border-box' }}
        />
        <button
          type="submit"
          className="btn btn-primary"
          style={{ width: '100%', padding: '0.9rem', fontWeight: 600, fontSize: '1.1rem', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #a8e063 0%, #56ab2f 100%)', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, boxShadow: '0 2px 8px rgba(86, 171, 47, 0.2)', transition: 'all 0.2s ease-in-out', marginBottom: 16 }}
          disabled={loading}
        >
          {loading ? t('creatingAccount') : t('signUp')}
        </button>
      </form>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, width: '100%' }}>
        <div ref={googleBtnRef} style={{ visibility: gsiRendered ? 'visible' : 'hidden' }} />
      </div>
      <div style={{ marginTop: 16, color: '#888' }}>
        {t('alreadyHaveAccount')} <a href="/login" style={{ color: '#56ab2f', textDecoration: 'underline', fontWeight: 500 }}>{t('logIn')}</a>
      </div>
    </div>
  );
};

export default SignupPage; 